/***
*
*   LOG/LIST
*   Combined Logs, Events & Errors tabbed view
*
*   Rewritten to match Figma node 330:26127
*
**********/

import { Fragment, useState, useEffect, useContext, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { convertToLocalTime } from '../../utility/helper';
import { ViewContext, Animate, useAPI } from 'components/lib';

const PAGE_SIZES = [25, 50, 100];

// Method badge colours
const METHOD_COLOURS = {
  POST:   { bg: '#faf5ff', color: '#9810fa' },
  PUT:    { bg: '#eff6ff', color: '#155dfc' },
  GET:    { bg: '#f0fdf4', color: '#16a34a' },
  PATCH:  { bg: '#fff7ed', color: '#ea580c' },
  DELETE: { bg: '#fef2f2', color: '#dc2626' },
};

function splitDateTime(str) {
  if (!str) return { date: '—', time: '' };
  const s = String(str);
  const idx = s.indexOf(' ');
  if (idx === -1) return { date: s, time: '' };
  return { date: s.substring(0, idx), time: s.substring(idx + 1) };
}

function MethodBadge({ method }) {
  if (!method) return <span style={{ color: '#9ca3af' }}>—</span>;
  const m = method.toUpperCase();
  const { bg, color } = METHOD_COLOURS[m] || { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{
      display: 'inline-block',
      background: bg,
      color,
      borderRadius: 28,
      padding: '4px 10px',
      fontSize: 12,
      fontWeight: 400,
      whiteSpace: 'nowrap',
    }}>
      {method.charAt(0).toUpperCase() + method.slice(1).toLowerCase()}
    </span>
  );
}

// Shared page-button style (matches users.js pattern)
const pageBtnStyle = (disabled) => ({
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  padding: '7px 13px',
  height: 35,
  cursor: disabled ? 'default' : 'pointer',
  color: disabled ? '#d1d5db' : '#111827',
  fontSize: 14,
  display: 'inline-flex',
  alignItems: 'center',
});

// ─── Shared filter UI components ──────────────────────────────────────────────

function FilterSectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 600, color: '#6b7280',
      textTransform: 'uppercase', letterSpacing: '0.06em',
      marginBottom: 8,
    }}>
      {children}
    </div>
  );
}

function ChipGroup({ options, selected, onToggle }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {options.map(opt => {
        const active = selected.includes(opt);
        return (
          <button
            key={opt}
            onClick={() => onToggle(opt)}
            style={{
              padding: '4px 14px',
              borderRadius: 999,
              border: active ? '1px solid #1974f9' : '1px solid #e5e7eb',
              background: active ? '#eff6ff' : '#f3f4f6',
              color: active ? '#1974f9' : '#374151',
              fontSize: 13, fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.1s',
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

const dateInputStyle = {
  width: '100%', height: 32, padding: '0 10px',
  border: '1px solid #e5e7eb', borderRadius: 6,
  fontSize: 13, color: '#374151', outline: 'none',
  boxSizing: 'border-box',
};

function DateRangeSection({ dateFrom, dateTo, onFromChange, onToChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, color: '#9ca3af', width: 28, flexShrink: 0 }}>From</span>
        <input
          type="date"
          value={dateFrom}
          onChange={e => onFromChange(e.target.value)}
          style={dateInputStyle}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, color: '#9ca3af', width: 28, flexShrink: 0 }}>To</span>
        <input
          type="date"
          value={dateTo}
          onChange={e => onToChange(e.target.value)}
          style={dateInputStyle}
        />
      </div>
    </div>
  );
}

function FilterPanelFooter({ onClear, onApply }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 24 }}>
      <button
        onClick={onClear}
        style={{
          background: 'none', border: 'none', padding: 0,
          fontSize: 13, color: '#6b7280', cursor: 'pointer',
          textDecoration: 'underline',
        }}
      >
        Clear all
      </button>
      <button
        onClick={onApply}
        style={{
          background: '#2563eb', color: '#fff', border: 'none',
          borderRadius: 8, padding: '8px 20px',
          fontSize: 14, fontWeight: 500, cursor: 'pointer',
        }}
      >
        Apply
      </button>
    </div>
  );
}

const filterPanelWrapStyle = {
  position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 200,
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
  width: 300,
  padding: 20,
};

// ─── LOGS FILTER ──────────────────────────────────────────────────────────────

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const STATUS_GROUPS = ['2xx', '3xx', '4xx', '5xx'];

const LOG_FILTER_DEFAULTS = {
  methods:    [],   // array of selected method strings
  statuses:   [],   // array of selected status-group strings
  dateFrom:   '',
  dateTo:     '',
};

function countActiveLogFilters(f) {
  let count = 0;
  if (f.methods.length > 0)                       count++;
  if (f.statuses.length > 0)                      count++;
  if (f.dateFrom !== '' || f.dateTo !== '')        count++;
  return count;
}

function LogFiltersPanel({ draft, setDraft, onApply, onClear }) {
  function toggleMethod(m) {
    setDraft(prev => ({
      ...prev,
      methods: prev.methods.includes(m)
        ? prev.methods.filter(x => x !== m)
        : [...prev.methods, m],
    }));
  }

  function toggleStatus(s) {
    setDraft(prev => ({
      ...prev,
      statuses: prev.statuses.includes(s)
        ? prev.statuses.filter(x => x !== s)
        : [...prev.statuses, s],
    }));
  }

  return (
    <div style={filterPanelWrapStyle}>
      {/* HTTP Method */}
      <div style={{ marginBottom: 20 }}>
        <FilterSectionLabel>HTTP Method</FilterSectionLabel>
        <ChipGroup options={HTTP_METHODS} selected={draft.methods} onToggle={toggleMethod} />
      </div>

      {/* Status code */}
      <div style={{ marginBottom: 20 }}>
        <FilterSectionLabel>Status Code</FilterSectionLabel>
        <ChipGroup options={STATUS_GROUPS} selected={draft.statuses} onToggle={toggleStatus} />
      </div>

      {/* Date range */}
      <div style={{ marginBottom: 4 }}>
        <FilterSectionLabel>Date Range</FilterSectionLabel>
        <DateRangeSection
          dateFrom={draft.dateFrom}
          dateTo={draft.dateTo}
          onFromChange={v => setDraft(p => ({ ...p, dateFrom: v }))}
          onToChange={v => setDraft(p => ({ ...p, dateTo: v }))}
        />
      </div>

      <FilterPanelFooter onClear={onClear} onApply={onApply} />
    </div>
  );
}

// Apply log filters to a row array
function applyLogFilters(rows, filters) {
  const f = filters;
  return rows.filter(row => {
    // HTTP method
    if (f.methods.length > 0) {
      const m = (row.method || '').toUpperCase();
      if (!f.methods.includes(m)) return false;
    }

    // Status code group
    if (f.statuses.length > 0) {
      const code = row.status_code ?? row.status ?? null;
      if (code === null || code === undefined) return false;
      const prefix = Math.floor(Number(code) / 100) + 'xx';
      if (!f.statuses.includes(prefix)) return false;
    }

    // Date range (row.time is like "2024-03-15 10:22:00")
    if (f.dateFrom !== '' || f.dateTo !== '') {
      const localTime = convertToLocalTime(row.time) || '';
      const dateStr = localTime ? localTime.split(' ')[0] : null;
      if (!dateStr) return false;
      if (f.dateFrom !== '' && dateStr < f.dateFrom) return false;
      if (f.dateTo   !== '' && dateStr > f.dateTo)   return false;
    }

    return true;
  });
}

// --- LOGS TAB ---

const LOG_COLS = [
  { key: 'time',     label: 'DATE CREATED', sortable: true  },
  { key: 'message',  label: 'MESSAGE',      sortable: false },
  { key: 'method',   label: 'METHOD',       sortable: true  },
  { key: 'endpoint', label: 'END POINT',    sortable: true  },
  { key: 'email',    label: 'EMAIL',        sortable: true  },
  { key: '_actions', label: 'ACTIONS',      sortable: false },
];

function LogsTab() {
  const context = useContext(ViewContext);

  const [page,      setPage]      = useState(1);
  const [pageSize,  setPageSize]  = useState(50);
  const [search,    setSearch]    = useState('');
  const [sortKey,   setSortKey]   = useState('time');
  const [sortDir,   setSortDir]   = useState('desc');
  const [openKebab, setOpenKebab] = useState(null);
  const [total,     setTotal]     = useState(0);

  // Filter state
  const [filtersOpen,    setFiltersOpen]    = useState(false);
  const [draftFilters,   setDraftFilters]   = useState(LOG_FILTER_DEFAULTS);
  const [appliedFilters, setAppliedFilters] = useState(LOG_FILTER_DEFAULTS);
  const filtersRef = useRef(null);
  const activeCount = countActiveLogFilters(appliedFilters);

  const offset = (page - 1) * pageSize;

  const isSearching = search.trim().length > 0;
  const queryParams = new URLSearchParams({ offset, limit: pageSize });
  if (search.trim()) queryParams.set('search', search.trim());
  const query = `/api/log?${queryParams}`;

  const logs = useAPI(query);

  useEffect(() => {
    if (logs.data?.total !== undefined) setTotal(logs.data.total);
  }, [logs.data]);

  // Reset to page 1 when search / page size / filters change
  useEffect(() => { setPage(1); }, [search, pageSize, appliedFilters]);

  // Close filter panel on outside click
  useEffect(() => {
    if (!filtersOpen) return;
    function handler(e) {
      if (filtersRef.current && !filtersRef.current.contains(e.target)) {
        setFiltersOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filtersOpen]);

  function openFilters() {
    setDraftFilters(appliedFilters);
    setFiltersOpen(true);
  }

  function handleApply() {
    setAppliedFilters(draftFilters);
    setFiltersOpen(false);
  }

  function handleClear() {
    setDraftFilters(LOG_FILTER_DEFAULTS);
    setAppliedFilters(LOG_FILTER_DEFAULTS);
    setFiltersOpen(false);
  }

  const rawRows = logs.data?.results || [];

  // Apply filters, then sort
  const filtered = applyLogFilters(rawRows, appliedFilters);

  const sorted = [...filtered].sort((a, b) => {
    let av = a[sortKey] ?? '';
    let bv = b[sortKey] ?? '';
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Server always paginates; client-side filters may reduce rows further
  const displayRows = sorted;
  const effectiveTotal = activeCount > 0 ? filtered.length : total;
  const totalPages = Math.max(1, Math.ceil(effectiveTotal / pageSize));

  function toggleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }

  function deleteLog(data, callback) {
    const multi = Array.isArray(data);
    const id = multi ? data.map(x => x.id) : data.id;
    context.modal.show({
      title: `Delete ${multi ? 'Logs' : 'Log'}`,
      form: { id: { type: 'hidden', value: id } },
      buttonText: 'Delete Log',
      text: `Are you sure you want to delete ${multi ? 'these logs' : 'this log'}?`,
      url: '/api/log/',
      method: 'DELETE',
      destructive: true,
    }, () => {
      callback();
      setTotal(t => t - 1);
    });
  }

  // Close kebab on outside click
  useEffect(() => {
    if (openKebab === null) return;
    function handler() { setOpenKebab(null); }
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openKebab]);

  return (
    <Fragment>

      {/* Search + Filters bar */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '20px 20px 0',
      }}>
        <div style={{ position: 'relative', width: 480 }}>
          <span style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: '#b4b9c1', pointerEvents: 'none', display: 'flex',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </span>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', paddingLeft: 44, paddingRight: 12, height: 37,
              border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 14,
              color: '#374151', outline: 'none', boxSizing: 'border-box',
              background: '#fff',
            }}
          />
        </div>

        {/* Filters button + panel */}
        <div ref={filtersRef} style={{ position: 'relative' }}>
          <button
            onClick={() => filtersOpen ? setFiltersOpen(false) : openFilters()}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '7px 13px', height: 35,
              border: activeCount > 0 ? '1px solid #1974f9' : '1px solid #e5e7eb',
              borderRadius: 10,
              background: activeCount > 0 ? '#eff6ff' : '#fff',
              fontSize: 14,
              color: activeCount > 0 ? '#1974f9' : '#374151',
              cursor: 'pointer',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            {activeCount > 0 ? `Filters · ${activeCount}` : 'Filters'}
          </button>

          {filtersOpen && (
            <LogFiltersPanel
              draft={draftFilters}
              setDraft={setDraftFilters}
              onApply={handleApply}
              onClear={handleClear}
            />
          )}
        </div>
      </div>

      {/* Table */}
      <div style={{ padding: '20px 20px 0' }}>
        <div style={{
          background: '#fff', border: '1px solid #e5e7eb',
          borderRadius: 14, overflow: 'hidden',
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
                  {/* Checkbox col */}
                  <th style={{ width: 36, padding: '12px 8px', textAlign: 'left' }}>
                    <div style={{
                      width: 16, height: 16,
                      border: '1px solid #e5e7eb', borderRadius: 4,
                    }} />
                  </th>
                  {LOG_COLS.map(col => (
                    <th
                      key={col.key}
                      onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                      style={{
                        padding: '12px 8px', textAlign: 'left',
                        fontWeight: 600, fontSize: 12, color: '#6a7282',
                        whiteSpace: 'nowrap', cursor: col.sortable ? 'pointer' : 'default',
                        userSelect: 'none', letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {col.label}
                        {col.sortable && (
                          <span style={{ color: sortKey === col.key ? '#374151' : '#d1d5db', fontSize: 11 }}>
                            {sortKey === col.key ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                          </span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.loading ? (
                  <tr>
                    <td colSpan={LOG_COLS.length + 1} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
                      Loading…
                    </td>
                  </tr>
                ) : displayRows.length === 0 ? (
                  <tr>
                    <td colSpan={LOG_COLS.length + 1} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
                      No logs found.
                    </td>
                  </tr>
                ) : displayRows.map(row => {
                  const localTime = convertToLocalTime(row.time) || '';
                  const { date, time } = splitDateTime(localTime);
                  return (
                    <tr
                      key={row.id}
                      style={{ borderBottom: '1px solid #f9fafb' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                    >
                      {/* Checkbox */}
                      <td style={{ width: 36, padding: '12px 8px' }}>
                        <div style={{
                          width: 16, height: 16,
                          border: '1px solid #e5e7eb', borderRadius: 4,
                        }} />
                      </td>

                      {/* Date Created */}
                      <td style={{ padding: '12px 8px', whiteSpace: 'nowrap', width: 122 }}>
                        <span style={{ display: 'block', fontSize: 12, color: '#111827', lineHeight: 1.5 }}>{date}</span>
                        {time && (
                          <span style={{ display: 'block', fontSize: 12, color: '#9ca3af', lineHeight: 1.5 }}>{time}</span>
                        )}
                      </td>

                      {/* Message */}
                      <td style={{ padding: '12px 8px', color: '#111827', fontSize: 14 }}>
                        {row.message || '—'}
                      </td>

                      {/* Method */}
                      <td style={{ padding: '12px 8px', width: 88 }}>
                        <MethodBadge method={row.method} />
                      </td>

                      {/* End Point */}
                      <td style={{ padding: '12px 8px', color: '#111827', fontSize: 14, width: 160, whiteSpace: 'nowrap' }}>
                        {row.endpoint || '—'}
                      </td>

                      {/* Email */}
                      <td style={{ padding: '12px 8px', color: '#111827', fontSize: 14, width: 200 }}>
                        {row.email || '—'}
                      </td>

                      {/* Actions */}
                      <td style={{ padding: '12px 8px', width: 76, position: 'relative', textAlign: 'center' }}>
                        <button
                          onClick={e => { e.stopPropagation(); setOpenKebab(openKebab === row.id ? null : row.id); }}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#364153', fontSize: 20, lineHeight: 1,
                            padding: '2px 6px', borderRadius: 4,
                          }}
                        >
                          ⋮
                        </button>
                        {openKebab === row.id && (
                          <div
                            onClick={e => e.stopPropagation()}
                            style={{
                              position: 'absolute', right: 8, top: '100%', zIndex: 100,
                              background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 140,
                            }}
                          >
                            <button
                              onClick={() => { window.location.href = `/logs/${row.id}`; }}
                              style={{
                                display: 'block', width: '100%', padding: '8px 16px',
                                background: 'none', border: 'none', textAlign: 'left',
                                fontSize: 14, color: '#374151', cursor: 'pointer',
                              }}
                            >
                              View
                            </button>
                            <button
                              onClick={() => { deleteLog(row, () => logs.refresh?.()); setOpenKebab(null); }}
                              style={{
                                display: 'block', width: '100%', padding: '8px 16px',
                                background: 'none', border: 'none', textAlign: 'left',
                                fontSize: 14, color: '#dc2626', cursor: 'pointer',
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 16px', borderTop: '1px solid #f3f4f6',
          }}>
            {/* Rows per page */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#111827' }}>
              <span>Rows per page</span>
              <select
                value={pageSize}
                onChange={e => setPageSize(Number(e.target.value))}
                style={{
                  border: '1px solid #e5e7eb', borderRadius: 10,
                  padding: '7px 13px', height: 35,
                  fontSize: 14, color: '#111827', background: '#fff', cursor: 'pointer',
                }}
              >
                {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* Page navigation */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#111827' }}>
              <span>Page</span>
              <input
                type="number"
                min={1}
                max={totalPages}
                value={page}
                onChange={e => {
                  const v = Number(e.target.value);
                  if (v >= 1 && v <= totalPages) setPage(v);
                }}
                style={{
                  width: 50, height: 35,
                  border: '1px solid #e5e7eb', borderRadius: 10,
                  padding: '7px 13px', fontSize: 14, textAlign: 'center',
                  boxSizing: 'border-box',
                }}
              />
              <span>of {totalPages}</span>
              <button onClick={() => setPage(1)}          disabled={page === 1}          style={pageBtnStyle(page === 1)}>«</button>
              <button onClick={() => setPage(p => p - 1)} disabled={page === 1}          style={pageBtnStyle(page === 1)}>‹</button>
              <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages} style={pageBtnStyle(page === totalPages)}>›</button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={pageBtnStyle(page === totalPages)}>»</button>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}

// ─── EVENTS TAB ───────────────────────────────────────────────────────────────

const COMMON_EVENTS = ['login', 'signup', 'prompt_submitted', 'document_upload_submitted'];

const EVENT_FILTER_DEFAULTS = {
  eventNames: [],
  dateFrom:   '',
  dateTo:     '',
};

function countActiveEventFilters(f) {
  let count = 0;
  if (f.eventNames.length > 0)                   count++;
  if (f.dateFrom !== '' || f.dateTo !== '')       count++;
  return count;
}

function EventFiltersPanel({ draft, setDraft, onApply, onClear }) {
  function toggleEvent(name) {
    setDraft(prev => ({
      ...prev,
      eventNames: prev.eventNames.includes(name)
        ? prev.eventNames.filter(x => x !== name)
        : [...prev.eventNames, name],
    }));
  }

  return (
    <div style={filterPanelWrapStyle}>
      <div style={{ marginBottom: 20 }}>
        <FilterSectionLabel>Event Name</FilterSectionLabel>
        <ChipGroup options={COMMON_EVENTS} selected={draft.eventNames} onToggle={toggleEvent} />
      </div>

      <div style={{ marginBottom: 4 }}>
        <FilterSectionLabel>Date Range</FilterSectionLabel>
        <DateRangeSection
          dateFrom={draft.dateFrom}
          dateTo={draft.dateTo}
          onFromChange={v => setDraft(p => ({ ...p, dateFrom: v }))}
          onToChange={v => setDraft(p => ({ ...p, dateTo: v }))}
        />
      </div>

      <FilterPanelFooter onClear={onClear} onApply={onApply} />
    </div>
  );
}

function applyEventFilters(rows, filters) {
  const f = filters;
  return rows.filter(row => {
    if (f.eventNames.length > 0) {
      if (!f.eventNames.includes(row.name)) return false;
    }
    if (f.dateFrom !== '' || f.dateTo !== '') {
      const localTime = convertToLocalTime(row.time) || '';
      const dateStr = localTime ? localTime.split(' ')[0] : null;
      if (!dateStr) return false;
      if (f.dateFrom !== '' && dateStr < f.dateFrom) return false;
      if (f.dateTo   !== '' && dateStr > f.dateTo)   return false;
    }
    return true;
  });
}

const EVENT_COLS = [
  { key: 'time',       label: 'DATE',       sortable: true  },
  { key: 'name',       label: 'EVENT NAME', sortable: true  },
  { key: 'user_email', label: 'USER EMAIL', sortable: true  },
  { key: 'metadata',   label: 'METADATA',   sortable: false },
];

function EventsTab() {
  const [page,      setPage]      = useState(1);
  const [pageSize,  setPageSize]  = useState(50);
  const [search,    setSearch]    = useState('');
  const [sortKey,   setSortKey]   = useState('time');
  const [sortDir,   setSortDir]   = useState('desc');
  const [total,     setTotal]     = useState(0);

  const [filtersOpen,    setFiltersOpen]    = useState(false);
  const [draftFilters,   setDraftFilters]   = useState(EVENT_FILTER_DEFAULTS);
  const [appliedFilters, setAppliedFilters] = useState(EVENT_FILTER_DEFAULTS);
  const filtersRef = useRef(null);
  const activeCount = countActiveEventFilters(appliedFilters);

  const offset = (page - 1) * pageSize;
  const eventQueryParams = new URLSearchParams({ offset, limit: pageSize });
  if (search.trim()) eventQueryParams.set('search', search.trim());
  const query = `/api/event?${eventQueryParams}`;

  const events = useAPI(query);

  useEffect(() => {
    if (events.data?.total !== undefined) setTotal(events.data.total);
  }, [events.data]);

  useEffect(() => { setPage(1); }, [search, pageSize, appliedFilters]);

  useEffect(() => {
    if (!filtersOpen) return;
    function handler(e) {
      if (filtersRef.current && !filtersRef.current.contains(e.target)) setFiltersOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filtersOpen]);

  function openFilters() { setDraftFilters(appliedFilters); setFiltersOpen(true); }
  function handleApply() { setAppliedFilters(draftFilters); setFiltersOpen(false); }
  function handleClear() {
    setDraftFilters(EVENT_FILTER_DEFAULTS);
    setAppliedFilters(EVENT_FILTER_DEFAULTS);
    setFiltersOpen(false);
  }

  const rawRows = events.data?.results || [];

  const filtered = applyEventFilters(rawRows, appliedFilters);

  const sorted = [...filtered].sort((a, b) => {
    let av = a[sortKey] ?? '';
    let bv = b[sortKey] ?? '';
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const effectiveTotal = activeCount > 0 ? filtered.length : total;
  const totalPages = Math.max(1, Math.ceil(effectiveTotal / pageSize));
  const displayRows = activeCount > 0
    ? sorted.slice((page - 1) * pageSize, page * pageSize)
    : sorted;

  function toggleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }

  return (
    <Fragment>
      {/* Search + Filters bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 0' }}>
        <div style={{ position: 'relative', width: 480 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#b4b9c1', pointerEvents: 'none', display: 'flex' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </span>
          <input
            type="text"
            placeholder="Search by event name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', paddingLeft: 44, paddingRight: 12, height: 37,
              border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 14,
              color: '#374151', outline: 'none', boxSizing: 'border-box',
              background: '#fff',
            }}
          />
        </div>

        <div ref={filtersRef} style={{ position: 'relative' }}>
          <button
            onClick={() => filtersOpen ? setFiltersOpen(false) : openFilters()}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '7px 13px', height: 35,
              border: activeCount > 0 ? '1px solid #1974f9' : '1px solid #e5e7eb',
              borderRadius: 10,
              background: activeCount > 0 ? '#eff6ff' : '#fff',
              fontSize: 14,
              color: activeCount > 0 ? '#1974f9' : '#374151',
              cursor: 'pointer',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            {activeCount > 0 ? `Filters · ${activeCount}` : 'Filters'}
          </button>
          {filtersOpen && (
            <EventFiltersPanel
              draft={draftFilters}
              setDraft={setDraftFilters}
              onApply={handleApply}
              onClear={handleClear}
            />
          )}
        </div>
      </div>

      {/* Table */}
      <div style={{ padding: '20px 20px 0' }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
                  {EVENT_COLS.map(col => (
                    <th
                      key={col.key}
                      onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                      style={{
                        padding: '12px 8px', textAlign: 'left',
                        fontWeight: 600, fontSize: 12, color: '#6a7282',
                        whiteSpace: 'nowrap', cursor: col.sortable ? 'pointer' : 'default',
                        userSelect: 'none', letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {col.label}
                        {col.sortable && (
                          <span style={{ color: sortKey === col.key ? '#374151' : '#d1d5db', fontSize: 11 }}>
                            {sortKey === col.key ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                          </span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {events.loading ? (
                  <tr>
                    <td colSpan={EVENT_COLS.length} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</td>
                  </tr>
                ) : displayRows.length === 0 ? (
                  <tr>
                    <td colSpan={EVENT_COLS.length} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No events found.</td>
                  </tr>
                ) : displayRows.map(row => {
                  const localTime = convertToLocalTime(row.time) || '';
                  const { date, time } = splitDateTime(localTime);
                  const meta = row.metadata
                    ? (typeof row.metadata === 'string' ? row.metadata : JSON.stringify(row.metadata)).slice(0, 80)
                    : null;
                  return (
                    <tr
                      key={row.id}
                      style={{ borderBottom: '1px solid #f9fafb' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                    >
                      {/* Date */}
                      <td style={{ padding: '12px 8px', whiteSpace: 'nowrap', width: 122 }}>
                        <span style={{ display: 'block', fontSize: 12, color: '#111827', lineHeight: 1.5 }}>{date}</span>
                        {time && <span style={{ display: 'block', fontSize: 12, color: '#9ca3af', lineHeight: 1.5 }}>{time}</span>}
                      </td>

                      {/* Event Name */}
                      <td style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          display: 'inline-block',
                          background: '#f0f4ff', color: '#2563eb',
                          borderRadius: 6, padding: '3px 10px',
                          fontSize: 12, fontWeight: 500,
                        }}>
                          {row.name || '—'}
                        </span>
                      </td>

                      {/* User Email */}
                      <td style={{ padding: '12px 8px', color: '#374151', fontSize: 14 }}>
                        {row.user_email || <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>

                      {/* Metadata (truncated) */}
                      <td style={{ padding: '12px 8px', color: '#6b7280', fontSize: 12, maxWidth: 300 }}>
                        {meta
                          ? <span style={{ fontFamily: 'monospace' }}>{meta}{meta.length >= 80 ? '…' : ''}</span>
                          : <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #f3f4f6' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#111827' }}>
              <span>Rows per page</span>
              <select
                value={pageSize}
                onChange={e => setPageSize(Number(e.target.value))}
                style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '7px 13px', height: 35, fontSize: 14, color: '#111827', background: '#fff', cursor: 'pointer' }}
              >
                {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#111827' }}>
              <span>Page</span>
              <input
                type="number" min={1} max={totalPages} value={page}
                onChange={e => { const v = Number(e.target.value); if (v >= 1 && v <= totalPages) setPage(v); }}
                style={{ width: 50, height: 35, border: '1px solid #e5e7eb', borderRadius: 10, padding: '7px 13px', fontSize: 14, textAlign: 'center', boxSizing: 'border-box' }}
              />
              <span>of {totalPages}</span>
              <button onClick={() => setPage(1)}          disabled={page === 1}          style={pageBtnStyle(page === 1)}>«</button>
              <button onClick={() => setPage(p => p - 1)} disabled={page === 1}          style={pageBtnStyle(page === 1)}>‹</button>
              <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages} style={pageBtnStyle(page === totalPages)}>›</button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={pageBtnStyle(page === totalPages)}>»</button>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}

// ─── ERROR LOGS TAB ────────────────────────────────────────────────────────────

const ERROR_STATUS_GROUPS = ['4xx', '5xx'];

const ERROR_FILTER_DEFAULTS = {
  statusGroups: [],
  dateFrom:     '',
  dateTo:       '',
};

function countActiveErrorFilters(f) {
  let count = 0;
  if (f.statusGroups.length > 0)                 count++;
  if (f.dateFrom !== '' || f.dateTo !== '')       count++;
  return count;
}

function ErrorFiltersPanel({ draft, setDraft, onApply, onClear }) {
  function toggleStatus(s) {
    setDraft(prev => ({
      ...prev,
      statusGroups: prev.statusGroups.includes(s)
        ? prev.statusGroups.filter(x => x !== s)
        : [...prev.statusGroups, s],
    }));
  }

  return (
    <div style={filterPanelWrapStyle}>
      <div style={{ marginBottom: 20 }}>
        <FilterSectionLabel>Status Code</FilterSectionLabel>
        <ChipGroup options={ERROR_STATUS_GROUPS} selected={draft.statusGroups} onToggle={toggleStatus} />
      </div>

      <div style={{ marginBottom: 4 }}>
        <FilterSectionLabel>Date Range</FilterSectionLabel>
        <DateRangeSection
          dateFrom={draft.dateFrom}
          dateTo={draft.dateTo}
          onFromChange={v => setDraft(p => ({ ...p, dateFrom: v }))}
          onToChange={v => setDraft(p => ({ ...p, dateTo: v }))}
        />
      </div>

      <FilterPanelFooter onClear={onClear} onApply={onApply} />
    </div>
  );
}

const ERROR_KEYWORDS = ['error', 'fail', 'exception'];

function isErrorRow(row) {
  const msg = (row.message || '').toLowerCase();
  const hasErrorKeyword = ERROR_KEYWORDS.some(k => msg.includes(k));
  const statusCode = row.status_code ?? row.status ?? null;
  const hasErrorStatus = statusCode !== null && Number(statusCode) >= 400;
  return hasErrorKeyword || hasErrorStatus;
}

function getSeverityGroup(row) {
  const code = row.status_code ?? row.status ?? null;
  if (code === null) return null;
  const n = Number(code);
  if (n >= 500) return '5xx';
  if (n >= 400) return '4xx';
  return null;
}

function SeverityBadge({ row }) {
  const group = getSeverityGroup(row);
  if (!group) return <span style={{ color: '#9ca3af' }}>—</span>;
  const is5xx = group === '5xx';
  return (
    <span style={{
      display: 'inline-block',
      background: is5xx ? '#fef2f2' : '#fff7ed',
      color: is5xx ? '#dc2626' : '#d97706',
      borderRadius: 6, padding: '3px 10px',
      fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {row.status_code ?? row.status ?? group}
    </span>
  );
}

function applyErrorFilters(rows, filters, search) {
  const f = filters;
  return rows.filter(row => {
    if (!isErrorRow(row)) return false;

    if (search.trim()) {
      const q = search.toLowerCase();
      const msg = (row.message || '').toLowerCase();
      const ep  = (row.endpoint || '').toLowerCase();
      const em  = (row.email || '').toLowerCase();
      if (!msg.includes(q) && !ep.includes(q) && !em.includes(q)) return false;
    }

    if (f.statusGroups.length > 0) {
      const group = getSeverityGroup(row);
      if (!group || !f.statusGroups.includes(group)) return false;
    }

    if (f.dateFrom !== '' || f.dateTo !== '') {
      const localTime = convertToLocalTime(row.time) || '';
      const dateStr = localTime ? localTime.split(' ')[0] : null;
      if (!dateStr) return false;
      if (f.dateFrom !== '' && dateStr < f.dateFrom) return false;
      if (f.dateTo   !== '' && dateStr > f.dateTo)   return false;
    }

    return true;
  });
}

const ERROR_COLS = [
  { key: 'time',       label: 'DATE',          sortable: true  },
  { key: 'message',    label: 'ERROR MESSAGE', sortable: false },
  { key: 'endpoint',   label: 'ENDPOINT',      sortable: true  },
  { key: 'email',      label: 'USER EMAIL',    sortable: true  },
  { key: '_status',    label: 'STATUS CODE',   sortable: false },
];

function ErrorLogsTab() {
  const [page,      setPage]      = useState(1);
  const [pageSize,  setPageSize]  = useState(50);
  const [search,    setSearch]    = useState('');
  const [sortKey,   setSortKey]   = useState('time');
  const [sortDir,   setSortDir]   = useState('desc');
  const [total,     setTotal]     = useState(0);

  const [filtersOpen,    setFiltersOpen]    = useState(false);
  const [draftFilters,   setDraftFilters]   = useState(ERROR_FILTER_DEFAULTS);
  const [appliedFilters, setAppliedFilters] = useState(ERROR_FILTER_DEFAULTS);
  const filtersRef = useRef(null);
  const activeCount = countActiveErrorFilters(appliedFilters);

  const offset = (page - 1) * pageSize;
  const errorQueryParams = new URLSearchParams({ errors_only: 'true', offset, limit: pageSize });
  if (search.trim()) errorQueryParams.set('search', search.trim());
  if (appliedFilters.dateFrom) errorQueryParams.set('date_from', appliedFilters.dateFrom);
  if (appliedFilters.dateTo) errorQueryParams.set('date_to', appliedFilters.dateTo);
  const logs = useAPI(`/api/log?${errorQueryParams}`);

  useEffect(() => {
    if (logs.data?.total !== undefined) setTotal(logs.data.total);
  }, [logs.data]);

  useEffect(() => { setPage(1); }, [search, pageSize, appliedFilters]);

  useEffect(() => {
    if (!filtersOpen) return;
    function handler(e) {
      if (filtersRef.current && !filtersRef.current.contains(e.target)) setFiltersOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filtersOpen]);

  function openFilters() { setDraftFilters(appliedFilters); setFiltersOpen(true); }
  function handleApply() { setAppliedFilters(draftFilters); setFiltersOpen(false); }
  function handleClear() {
    setDraftFilters(ERROR_FILTER_DEFAULTS);
    setAppliedFilters(ERROR_FILTER_DEFAULTS);
    setFiltersOpen(false);
  }

  const rawRows = logs.data?.results || [];

  // Server handles errors_only, search, and date range.
  // Only status code group filter (4xx/5xx) remains client-side.
  const filtered = appliedFilters.statusGroups.length > 0
    ? rawRows.filter(row => {
        const group = getSeverityGroup(row);
        return group && appliedFilters.statusGroups.includes(group);
      })
    : rawRows;

  const sorted = [...filtered].sort((a, b) => {
    let av = a[sortKey] ?? '';
    let bv = b[sortKey] ?? '';
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const displayRows = sorted;

  function toggleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }

  return (
    <Fragment>
      {/* Search + Filters bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 0' }}>
        <div style={{ position: 'relative', width: 480 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#b4b9c1', pointerEvents: 'none', display: 'flex' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </span>
          <input
            type="text"
            placeholder="Search by message, endpoint or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', paddingLeft: 44, paddingRight: 12, height: 37,
              border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 14,
              color: '#374151', outline: 'none', boxSizing: 'border-box',
              background: '#fff',
            }}
          />
        </div>

        <div ref={filtersRef} style={{ position: 'relative' }}>
          <button
            onClick={() => filtersOpen ? setFiltersOpen(false) : openFilters()}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '7px 13px', height: 35,
              border: activeCount > 0 ? '1px solid #1974f9' : '1px solid #e5e7eb',
              borderRadius: 10,
              background: activeCount > 0 ? '#eff6ff' : '#fff',
              fontSize: 14,
              color: activeCount > 0 ? '#1974f9' : '#374151',
              cursor: 'pointer',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            {activeCount > 0 ? `Filters · ${activeCount}` : 'Filters'}
          </button>
          {filtersOpen && (
            <ErrorFiltersPanel
              draft={draftFilters}
              setDraft={setDraftFilters}
              onApply={handleApply}
              onClear={handleClear}
            />
          )}
        </div>
      </div>

      {/* Table */}
      <div style={{ padding: '20px 20px 0' }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
                  {ERROR_COLS.map(col => (
                    <th
                      key={col.key}
                      onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                      style={{
                        padding: '12px 8px', textAlign: 'left',
                        fontWeight: 600, fontSize: 12, color: '#6a7282',
                        whiteSpace: 'nowrap', cursor: col.sortable ? 'pointer' : 'default',
                        userSelect: 'none', letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {col.label}
                        {col.sortable && (
                          <span style={{ color: sortKey === col.key ? '#374151' : '#d1d5db', fontSize: 11 }}>
                            {sortKey === col.key ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                          </span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.loading ? (
                  <tr>
                    <td colSpan={ERROR_COLS.length} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</td>
                  </tr>
                ) : displayRows.length === 0 ? (
                  <tr>
                    <td colSpan={ERROR_COLS.length} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No error logs found.</td>
                  </tr>
                ) : displayRows.map(row => {
                  const localTime = convertToLocalTime(row.time) || '';
                  const { date, time } = splitDateTime(localTime);
                  return (
                    <tr
                      key={row.id}
                      style={{ borderBottom: '1px solid #f9fafb' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                    >
                      {/* Date */}
                      <td style={{ padding: '12px 8px', whiteSpace: 'nowrap', width: 122 }}>
                        <span style={{ display: 'block', fontSize: 12, color: '#111827', lineHeight: 1.5 }}>{date}</span>
                        {time && <span style={{ display: 'block', fontSize: 12, color: '#9ca3af', lineHeight: 1.5 }}>{time}</span>}
                      </td>

                      {/* Error Message */}
                      <td style={{ padding: '12px 8px', color: '#111827', fontSize: 14 }}>
                        {row.message || '—'}
                      </td>

                      {/* Endpoint */}
                      <td style={{ padding: '12px 8px', color: '#374151', fontSize: 13, whiteSpace: 'nowrap', width: 180 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <MethodBadge method={row.method} />
                          <span>{row.endpoint || '—'}</span>
                        </div>
                      </td>

                      {/* User Email */}
                      <td style={{ padding: '12px 8px', color: '#374151', fontSize: 14, width: 200 }}>
                        {row.email || <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>

                      {/* Status Code */}
                      <td style={{ padding: '12px 8px', width: 100 }}>
                        <SeverityBadge row={row} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #f3f4f6' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#111827' }}>
              <span>Rows per page</span>
              <select
                value={pageSize}
                onChange={e => setPageSize(Number(e.target.value))}
                style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '7px 13px', height: 35, fontSize: 14, color: '#111827', background: '#fff', cursor: 'pointer' }}
              >
                {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#111827' }}>
              <span>Page</span>
              <input
                type="number" min={1} max={totalPages} value={page}
                onChange={e => { const v = Number(e.target.value); if (v >= 1 && v <= totalPages) setPage(v); }}
                style={{ width: 50, height: 35, border: '1px solid #e5e7eb', borderRadius: 10, padding: '7px 13px', fontSize: 14, textAlign: 'center', boxSizing: 'border-box' }}
              />
              <span>of {totalPages}</span>
              <button onClick={() => setPage(1)}          disabled={page === 1}          style={pageBtnStyle(page === 1)}>«</button>
              <button onClick={() => setPage(p => p - 1)} disabled={page === 1}          style={pageBtnStyle(page === 1)}>‹</button>
              <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages} style={pageBtnStyle(page === totalPages)}>›</button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={pageBtnStyle(page === totalPages)}>»</button>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}

// --- ROOT COMPONENT ---

const PATH_TO_TAB = {
  '/logs':        'logs',
  '/logs/events': 'events',
  '/logs/errors': 'errors',
};

export function Logs() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const activeTab = PATH_TO_TAB[pathname] ?? 'logs';

  const tabStyle = (active) => ({
    display: 'inline-flex', alignItems: 'center',
    padding: '12px 20px', cursor: 'pointer',
    fontSize: 14, fontWeight: 600, lineHeight: 1.5,
    background: 'none', border: 'none', outline: 'none',
    borderBottom: active ? '2px solid #1974f9' : '2px solid transparent',
    color: active ? '#1974f9' : '#6a7282',
    marginBottom: -1,
  });

  return (
    <Animate>

      {/* Page header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0, lineHeight: 1.5 }}>
          System Logs
        </h1>
        <p style={{ fontSize: 16, color: '#6a7282', margin: '0', lineHeight: 1.5 }}>
          Monitor and track all system activities
        </p>
      </div>

      {/* Page section card */}
      <div style={{
        background: '#fff', border: '1px solid #e5e7eb',
        borderRadius: 14, overflow: 'hidden',
      }}>

        {/* Tab bar */}
        <div style={{
          borderBottom: '1px solid #e5e7eb',
          display: 'flex', paddingLeft: 0,
        }}>
          <button style={tabStyle(activeTab === 'logs')}      onClick={() => navigate('/logs')}>
            Prompt
          </button>
          <button style={tabStyle(activeTab === 'events')}    onClick={() => navigate('/logs/events')}>
            Events
          </button>
          <button style={tabStyle(activeTab === 'errors')}    onClick={() => navigate('/logs/errors')}>
            Errors
          </button>
        </div>

        {/* Tab content */}
        <div style={{ paddingBottom: 20 }}>
          {activeTab === 'logs'      && <LogsTab />}
          {activeTab === 'events'    && <EventsTab />}
          {activeTab === 'errors'    && <ErrorLogsTab />}
        </div>

      </div>

    </Animate>
  );
}
